import { RefObject } from 'react'
import { Assets, MenuGroup, MenuItem } from '@nexds/web'
import { useTheme } from 'styled-components'

import { Waypoint } from '@/domain/models'
import { Coordinates } from '@/domain/protocols/Coordinates'

import {
  ActionButton,
  ActionButtonsWrapper,
  ListContainer,
  ResultWrapper,
  SearchListContainer
} from './SearchList.styles'

import { useI18n } from '@/presentation/hooks/useI18n'
import { getAdhpInfoPath } from '@/presentation/modules/AdhpInfo/utils/adhpInfoNavigation'
import { getProcedureChartsPath } from '@/presentation/modules/AdhpInfo/utils/procedureChartsNavigation'
import { isGenericCoordWaypoint } from '@/presentation/modules/UserWaypointsScreen/utils/userWaypointNavigation'
import { openDrawer } from '@/presentation/router/DrawerRouter'
import { RouterPaths } from '@/presentation/router/RouterPathsMapper'

interface SearchListProps {
  waypoints: Waypoint[]
  maxHeight: number
  onWaypointClick: (waypoint: Waypoint) => void
  onLocateWaypointClick: (coordinates: Coordinates) => void
  onCreateUserWaypointClick?: (waypoint: Waypoint) => void
  onInfoClick?: () => void
  currentSearchResult?: number
  searchResults?: Waypoint[]
  resultsRef: RefObject<HTMLDivElement>
}

function SearchList(props: SearchListProps) {
  const { waypoints, maxHeight, onWaypointClick, onLocateWaypointClick, onCreateUserWaypointClick, onInfoClick } = props
  const theme = useTheme()
  const { t } = useI18n()

  const handleShowInfo = (infoCode: string) => {
    onInfoClick?.()
    openDrawer(getAdhpInfoPath({ code: infoCode, tab: 'rotaer' }))
  }

  const handleShowProcedures = (infoCode: string) => {
    onInfoClick?.()
    openDrawer(getProcedureChartsPath(infoCode))
  }

  const handleShowServices = (infoCode: string) => {
    const normalizedIcao = infoCode.trim().toUpperCase()
    if (!normalizedIcao) return

    onInfoClick?.()
    openDrawer(`${RouterPaths.MARKET}?icao=${encodeURIComponent(normalizedIcao)}`)
  }

  return (
    <SearchListContainer>
      <MenuGroup isLightStyle>
        <ListContainer maxHeight={maxHeight} ref={props.resultsRef}>
          {waypoints &&
            waypoints.map((waypoint) => (
              <ResultWrapper
                key={waypoint.id.toString()}
                isSelected={props.searchResults && waypoint.id === props.searchResults[props.currentSearchResult]?.id}
              >
                <MenuItem
                  size="sm"
                  isLightStyle
                  title={waypoint.getDisplayName()}
                  description={waypoint.extra?.isAddress ? undefined : waypoint.getTypeName()}
                  onClick={() => onWaypointClick(waypoint)}
                />
                <ActionButtonsWrapper>
                  {onCreateUserWaypointClick && isGenericCoordWaypoint(waypoint) && (
                    <ActionButton
                      onClick={() => onCreateUserWaypointClick(waypoint)}
                      title={t('USER-WAYPOINT_CREATE-FROM-COORDINATES')}
                    >
                      <Assets.Plus color={theme.colors.secondaryL2} size={'sm'} />
                    </ActionButton>
                  )}
                  {waypoint.waypointType?.codeName === 'ADHP' && (
                    <>
                      <ActionButton
                        onClick={() => (waypoint.code ? handleShowInfo(waypoint.code) : undefined)}
                        title={t('MAP-POINTS_MODAL_INFO-AERODROME')}
                      >
                        <Assets.Info color={theme.colors.secondaryL2} size={'sm'} />
                      </ActionButton>
                      <ActionButton
                        onClick={() => (waypoint.code ? handleShowProcedures(waypoint.code) : undefined)}
                        title={t('MAP-POINTS_MODAL_PROCEDURES')}
                      >
                        <Assets.Charts color={theme.colors.secondaryL2} size={'sm'} />
                      </ActionButton>
                      <ActionButton
                        onClick={() => (waypoint.code ? handleShowServices(waypoint.code) : undefined)}
                        title={t('MAP-POINTS_MODAL_VIEW-SERVICES')}
                      >
                        <Assets.Market color={theme.colors.secondaryL2} size={'sm'} />
                      </ActionButton>
                    </>
                  )}
                  <ActionButton
                    onClick={() => onLocateWaypointClick?.(waypoint.coordinates)}
                    // data-tooltip-id={tooltipIds.secondary.universal}
                    // data-tooltip-content="Mostrar no mapa"
                    // data-tooltip-place="bottom"
                    title="Mostrar no mapa" // TODO: i18n
                  >
                    <Assets.CenterPoint color={theme.colors.secondaryL2} size={'sm'} />
                  </ActionButton>
                </ActionButtonsWrapper>
              </ResultWrapper>
            ))}
        </ListContainer>
      </MenuGroup>
    </SearchListContainer>
  )
}

export { SearchList }
